import { RiCoupon3Line } from "react-icons/ri";
import AddCoupon from "../../components/Coupons/AddCoupon";
import { useEffect, useState } from "react";
import CouponCard from "../../components/Coupons/CouponCard";
import GridTableCoupons from "../../components/Coupons/GridTableCoupons";
import CouponsStatusPreview from "../../components/Coupons/CouponsStatusPreview";
import { useSelector } from "react-redux";
import axios from "axios";

import ErrorMessage from "../../components/ErrorMessage";
import toast from "react-hot-toast";
import { coupon } from "../../types/globalTypes";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useTranslation } from "react-i18next";

function CouponsSettings() {
    const { t, i18n } = useTranslation();
    const lang = i18n.language;

  const [coupons, setCoupons] = useState<coupon[]>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<null | string>(null);
  const [needRefetch, setNeedRefetch] = useState(false);

  const BASE_URL = import.meta.env.VITE_BASE_URL;
  const token = useSelector(
    (state: { auth: { token: string } }) => state.auth.token,
  );

  useEffect(() => {
    async function getCoupons() {
      const url = `${BASE_URL}/api/dashboard/coupons?locale=${lang}`;

      setIsLoading(true);
      setError(null);
      setNeedRefetch(false);

      try {
        const response = await axios.get(url, {
          headers: {
            Authorization: `${token}`,
          },
        });

        if (!response.data.success) {
          setError(response.data.message);
          setIsLoading(false);
          return;
        }

        setCoupons(response.data?.coupons);
      } catch (error: unknown) {
        if (axios.isAxiosError(error)) {
          const message =
            error.response?.data?.error ||
            (t('coupons.settingsUpdateError') as string);
          setError(message);
          toast.error(message);
        } else {
          setError(
            t("coupons.unexpectedError") as string,
          );
          toast.error(
            t("coupons.unexpectedError")  as string,
          );
        }
      } finally {
        setIsLoading(false);
      }
    }

    getCoupons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needRefetch]);

  if (isLoading)
    return (
      <>
        <div className="flex flex-col gap-6">
          <Skeleton className="ms-auto h-[50px] w-[100px] dark:bg-slate-600 dark:bg-opacity-35" />
          <Skeleton className="h-[200px] w-full dark:bg-slate-600 dark:bg-opacity-35" />
        </div>
      </>
    );
  if (error) return <ErrorMessage msg={error} />;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="self-start text-2xl font-semibold">
        {t("coupons.settingsTitle")} 
        </h1>
        <AddCoupon setNeedRefetch={setNeedRefetch} />
      </div>

      <div className="flex flex-col gap-5 rounded-md border border-gray-300 px-4 py-6">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-3">
            <RiCoupon3Line className="text-xl text-gray-700" />
            <span className="text-xl">
              {t("coupons.sectionTitle")}
            </span>
          </div>
        </div>

        <CouponsStatusPreview />

        {coupons?.length === 0 ? <>
      <Alert className="bg-skyblue dark:bg-skyblue text-sidebar dark:text-sidebar border-none dark:border-none">
        <AlertTitle className="text-xl text-center">{t("coupons.noCouponsFound")}</AlertTitle>
        <AlertDescription className="text-center my-5 font-semibold">
        {t("coupons.noCouponsMessage")}
        </AlertDescription>
      </Alert>
    </> : (
          <GridTableCoupons>
            {coupons?.map((coupon, index) => (
              <CouponCard
                setNeedRefetch={setNeedRefetch}
                key={index}
                _id={coupon._id}
                active={coupon.active}
                amount={coupon.amount}
                code={coupon.code}
                endDate={coupon.endDate}
                startDate={coupon.startDate}
                type={coupon.type}
                limitPerUser={coupon.limitPerUser}
                limit={coupon.limit}
                expired={coupon.expired}
                leftUses={coupon.leftUses}
                limitExceeded={coupon.limitExceeded}
                usedCount={coupon.usedCount}
              />
            ))}
          </GridTableCoupons>
        )}
      </div>
    </div>
  );
}

export default CouponsSettings;
